import { useMemo, useState } from 'react'
import { groupableColumns, groupFlatReport, collectLeafRows, stripHtml, toNumber, formatLikeSample, summableColumns } from './reportGridUtils'
import { escapeHtml } from './escapeHtml'
import { trackGridTransform } from '@/analytics/gridTransforms'
import PopoverMenu from '@components/common/PopoverMenu'
import { ChevronDownIcon } from './queue/queueIcons'

/**
 * CC — shared "Group By + column-value filter" toolbar for the client-shaped
 * report grids (CsvReportGrid + AutoReportGrid), extracted from CsvReportGrid
 * (CC-49596/49346/49349) so both surfaces expose identical controls.
 *
 * Operates on the GENERIC report shape ({ columns, reportData,
 * reportFooterCells }) — it never touches a component's upstream data source,
 * so each adapter keeps its own reshaping (ghost columns, total-row promotion,
 * auto columns, ...) and layers these controls on the finished report.
 *
 *   - Group By: options come from groupableColumns (visible, non-numeric, labeled).
 *     Legacy grouped CSVs only print the group value on the first row of each
 *     block, so blanks in the grouped column are forward-filled before
 *     groupFlatReport buckets the leaves (SUM-only subtotals per CC-49382).
 *   - Value filter: leaf rows are matched on the chosen column's trimmed
 *     displayValue. While a filter is active, footer totals are RECOMPUTED over
 *     the filtered rows for SUM-safe columns (CC-49354 #6 — consistent with the
 *     grids' CC-49456 default); non-summable numeric cells are blanked (never
 *     stale, never fabricated) and a "(N)"-style row count follows the filter.
 *
 * @param {object} report generic { columns, reportData, reportFooterCells }
 * @param {string} [storageKey] the host grid's per-page key, used ONLY to
 *   attribute the analytics transform to a report (spec 094). These controls
 *   are in-memory and persist nothing, so this is not storage.
 * @returns {{ controls: JSX.Element|null, displayReport: object, grouped: boolean, groupBy: string }}
 */
const useGridControls = (report, storageKey) => {
  // Client-side Group By (e.g. practice area on Timekeeper Analysis).
  const [groupBy, setGroupBy] = useState('')
  // Client-side column-value filter (e.g. Position, Originator). Only offered
  // for text columns the report actually carries.
  //
  // CC-50236: filterCol holds the column ID, not the label. It used to hold the
  // label and resolve via `groupByOptions.find((c) => c.label === filterCol)`,
  // which is wrong two ways: two columns sharing a label silently collide (find
  // returns the first, so filtering "Name" could filter the wrong Name), and the
  // label is about to stop being stable at all once a firm can rename a column.
  // Group By was already id-based; this brings the value filter into line.
  const [filterCol, setFilterCol] = useState('')
  const [filterVal, setFilterVal] = useState('')

  const groupByOptions = useMemo(() => groupableColumns(report?.columns || []), [report])

  const filterColId = useMemo(() => {
    if (!filterCol) return null
    // Still resolved against the live column set, so a stale selection (a column
    // the current report no longer carries) falls back to "no filter" rather
    // than silently matching nothing.
    const col = groupByOptions.find((c) => String(c.columnId) === filterCol)
    return col ? String(col.columnId) : null
  }, [filterCol, groupByOptions])

  const cellFor = (row, colId) => row.dataCells?.find((c) => String(c.columnId) === colId)

  const filterValues = useMemo(() => {
    if (filterColId == null) return []
    const seen = new Set()
    for (const row of collectLeafRows(report?.reportData || [])) {
      const v = stripHtml(cellFor(row, filterColId)?.displayValue ?? '').trim()
      if (v) seen.add(v)
    }
    return [...seen].sort((a, b) => a.localeCompare(b))
  }, [report, filterColId])

  const filtering = !!(filterColId != null && filterVal)

  const displayReport = useMemo(() => {
    let out = report
    if (filtering) {
      const allLeaves = collectLeafRows(out?.reportData || [])
      const kept = allLeaves.filter(
        (row) => stripHtml(cellFor(row, filterColId)?.displayValue ?? '').trim() === filterVal
      )
      // CC-49354 #6: recompute SUM-only totals for the filtered subset instead of
      // suppressing the whole footer (consistency with the grids' CC-49456
      // behavior). The sum-equals-total guardrail (summableColumns) decides which
      // cells are true plain sums; non-summable numeric cells are blanked —
      // never stale, never fabricated. A text label cell carrying a "(N)" row
      // count (AutoReportGrid's "Total (140)") follows the filter too.
      const footer = out?.reportFooterCells || []
      const serverTotals = new Map()
      for (const c of footer) {
        const n = toNumber(c.displayValue)
        if (n != null) serverTotals.set(String(c.columnId), n)
      }
      const allValues = new Map()
      for (const id of serverTotals.keys()) {
        allValues.set(id, allLeaves
          .map((r) => toNumber(cellFor(r, id)?.dataValue ?? cellFor(r, id)?.displayValue))
          .filter((n) => n != null))
      }
      const summable = summableColumns(allValues, serverTotals)
      const reportFooterCells = footer.map((c) => {
        const id = String(c.columnId)
        if (summable.has(id)) {
          const sum = kept.reduce((acc, r) => {
            const n = toNumber(cellFor(r, id)?.dataValue ?? cellFor(r, id)?.displayValue)
            return acc + (n ?? 0)
          }, 0)
          return { ...c, displayValue: formatLikeSample(c.displayValue)(sum) }
        }
        if (serverTotals.has(id)) return { ...c, displayValue: '' }
        return { ...c, displayValue: String(c.displayValue ?? '').replace(/\(\s*\d[\d,]*\s*\)/, `(${kept.length})`) }
      })
      // CC-49860: a multi-row footer must not survive a value filter half-stale.
      // reportFooterCells above is recomputed for the kept rows, but the extra rows
      // (a "Total / YTD" line under a "Total / PTD" one) are verbatim server figures
      // for the FULL dataset that cannot be honestly recomputed — leaving them
      // beneath a filtered row 0 would put two contradictory totals side by side.
      // Drop them while filtering; the "(totals recomputed…)" note already explains
      // that the footer describes the visible rows.
      const reportFooterRows = out?.reportFooterRows?.length > 1 ? [reportFooterCells] : out?.reportFooterRows
      out = { ...out, reportData: kept, reportFooterCells, ...(reportFooterRows ? { reportFooterRows } : {}) }
    }
    if (groupBy) {
      // Forward-fill blanks in the grouped column so continuation rows land in
      // the right bucket instead of the (blank) bucket (legacy grouped CSVs).
      let last = ''
      const filled = {
        ...out,
        reportData: collectLeafRows(out?.reportData || []).map((row) => {
          const v = stripHtml(cellFor(row, String(groupBy))?.displayValue ?? '').trim()
          if (v) { last = v; return row }
          if (!last) return row
          // Spec 193: `last` is decoded text and displayValue is HTML — escape
          // it, or a filled cell renders the markup its source cell escaped.
          return {
            ...row,
            dataCells: row.dataCells.map((c) =>
              String(c.columnId) === String(groupBy) ? { ...c, displayValue: escapeHtml(last) } : c
            ),
          }
        }),
      }
      out = groupFlatReport(filled, groupBy)
    }
    return out
  }, [report, filtering, filterColId, filterVal, groupBy])

  /*
   * Group by / Filter — real dropdowns, in the grid header (Jordan, 2026-08-26).
   *
   * Round 2 put these in the query card as .btn-rc-pill labels wrapping native
   * <select>s. Two things were wrong with that and Jordan caught both.
   *
   * WHERE: beside Reset and Run they crowded a card that is about the QUERY,
   * while these two transform the RESULT. They now sit on their own row at the
   * top of the result card, above the record count — which is also the only
   * place with room for a long column name plus its value dropdown.
   *
   * WHAT: they were native <select>s, so the OPEN list was drawn by the OS and
   * looked nothing like Display View, Column Filter or Export — the "odd inner
   * box". index.css said the open list "CANNOT" be restyled, and that is true of
   * a <select>; the answer is not to style it but to stop using one. They are
   * PopoverMenu now, the same primitive every other dropdown in this chrome uses,
   * with role="menuitemradio" rows.
   *
   * The earlier revert this reverses is real and worth naming: an attempt to put
   * a <select> INSIDE a role="menu" popover was backed out because a select is
   * keyboard-inoperable under roving menu focus. That is not this. There is no
   * select any more — the options ARE the menu rows, so roving focus is exactly
   * what should own the arrow keys, and PopoverMenu already implements
   * ArrowUp/Down/Home/End, Escape, click-outside and focus restore.
   *
   * aria-checked marks the current choice, and PopoverMenu closes on a radio
   * click (menuitemCHECKBOX deliberately does not — Display View is multi-select).
   */
  const menuRow = (checked, onSelect, key, children) => (
    <button
      key={key}
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      onClick={onSelect}
      className={`flex w-full items-center gap-2 px-3.5 py-[7px] text-[13px] hover:bg-gray-50 ${checked ? 'font-semibold text-rc-chip-text' : 'text-rc-ink-2'}`}
    >
      <span aria-hidden="true" className="w-3 shrink-0 text-[11px] leading-none">{checked ? '\u2713' : ''}</span>
      <span className="truncate">{children}</span>
    </button>
  )

  const labelFor = (id, fallback) =>
    groupByOptions.find((c) => String(c.columnId) === String(id))?.label || fallback

  // The trigger reads "Group by: Practice Area" — the caption is part of the
  // control, not a separate text node, so the row stays one thing to scan.
  const trigger = (caption, value) => (
    <>
      <span className="text-rc-muted">{caption}</span>
      <span className="max-w-[14rem] truncate">{value}</span>
      {/* B6 (spec 117) \u2014 outline chevron, was a filled '\u25BE' written as an
          escape, which is why a literal-glyph grep across the codebase missed
          this site entirely. ONE edit here reaches all three triggers this
          helper builds (Group by / Filter / is:).

          The old glyph was `text-[10px] text-rc-muted`: 10px of triangle in a
          14px pill label, in the muted grey. It is now 14px and currentColor,
          i.e. --color-rc-ink-2, matching `.select-rc`'s chevron in size and the
          pill's own label in weight. */}
      <ChevronDownIcon className="h-3.5 w-3.5 flex-none" />
    </>
  )

  const controls = groupByOptions.length === 0 ? null : (
    <div data-print-hide className="flex flex-wrap items-center gap-2">
      <PopoverMenu
        role="menu"
        label="Group by"
        triggerLabel="Group by"
        data-pendo="report-client-group-by"
        triggerClassName="btn-rc-pill"
        triggerContent={trigger('Group by:', groupBy ? labelFor(groupBy, 'None') : 'None')}
      >
        {menuRow(!groupBy, () => {
          trackGridTransform(storageKey, 'client_group_by', 'none')
          setGroupBy('')
        }, 'none', 'None')}
        {groupByOptions.map((c) =>
          menuRow(String(groupBy) === String(c.columnId), () => {
            // The column ID, never its label — a firm can rename a column.
            trackGridTransform(storageKey, 'client_group_by', String(c.columnId))
            setGroupBy(String(c.columnId))
          }, c.columnId, c.label)
        )}
      </PopoverMenu>

      <PopoverMenu
        role="menu"
        label="Filter column"
        triggerLabel="Filter column"
        data-pendo="report-client-col-filter"
        triggerClassName="btn-rc-pill"
        triggerContent={trigger('Filter:', filterCol ? labelFor(filterCol, 'None') : 'None')}
      >
        {menuRow(!filterCol, () => {
          trackGridTransform(storageKey, 'client_col_filter', 'none')
          setFilterCol(''); setFilterVal('')
        }, 'none', 'None')}
        {groupByOptions.map((c) =>
          menuRow(String(filterCol) === String(c.columnId), () => {
            trackGridTransform(storageKey, 'client_col_filter', String(c.columnId))
            setFilterCol(String(c.columnId)); setFilterVal('')
          }, c.columnId, c.label)
        )}
      </PopoverMenu>

      {filterCol && (
        <PopoverMenu
          role="menu"
          label="Filter value"
          triggerLabel="Filter value"
          triggerClassName="btn-rc-pill"
          triggerContent={trigger('is:', filterVal || 'All values')}
        >
          {menuRow(!filterVal, () => setFilterVal(''), 'all', 'All values')}
          {filterValues.map((v) => menuRow(filterVal === v, () => setFilterVal(v), v, v))}
        </PopoverMenu>
      )}

      {/* Verbatim: AutoReportGrid.controls.test.jsx matches this by regex, and it
          is the sentence that explains why the footer describes only the visible
          rows. text-rc-muted is 4.83:1 (was text-gray-400, #9ca3af at 2.54:1). */}
      {filtering && (
        <span className="text-[11px] text-rc-muted">(totals recomputed for the filtered rows — SUM columns only)</span>
      )}
    </div>
  )

  return { controls, displayReport, grouped: !!groupBy, groupBy }
}

export default useGridControls
